// DateDraftScope.js
//
// A half-typed date is not a date. AppDatePicker emits '' for one and keeps
// the draft on screen, so the value above says "no filter" while the field
// says 05/MM/YYYY. The action that would submit that state has to know the
// draft exists and turn the field red — without every host tracking each of
// its pickers by hand (six in the data filters, index-shifting ones in a
// function's data source, two layers down from Run in the dashboards).
//
// A scope collects the partial pickers beneath it. The host keeps a ref and
// calls `reveal()` from Apply / Run / Save: it returns whether anything is
// partial and, from then on, the pickers in the scope show their incomplete
// state until each is completed or cleared. `onPartialChange` lets a host
// enable its Clear button or drop its message as drafts come and go.
//
// A picker outside any scope keeps the plain contract: '' out, no red.

import React, {
  createContext,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

export const DateDraftContext = createContext(null);

// What every host says when it refuses to proceed.
export const INCOMPLETE_DATES_MESSAGE = 'Complete or clear the highlighted dates first.';

const DateDraftScope = forwardRef(function DateDraftScope({ children, onPartialChange }, ref) {
  const partialIds = useRef(new Set());
  const [hasPartial, setHasPartial] = useState(false);
  const [revealed, setRevealed] = useState(false);

  // Stable, so a picker's registration effect only re-runs when its own
  // partial state changes.
  const report = useCallback((id, isPartial) => {
    const ids = partialIds.current;
    if (isPartial) ids.add(id); else ids.delete(id);
    setHasPartial(ids.size > 0);
  }, []);

  const onPartialChangeRef = useRef(onPartialChange);
  onPartialChangeRef.current = onPartialChange;
  useEffect(() => {
    onPartialChangeRef.current?.(hasPartial);
  }, [hasPartial]);

  // A scope that unmounts takes its drafts with it (a mode switch, a card
  // removed): the host's last word from it must be "nothing partial".
  useEffect(() => () => {
    onPartialChangeRef.current?.(false);
  }, []);

  useImperativeHandle(ref, () => ({
    // True when a half-typed date is present; those pickers now show it.
    reveal: () => {
      setRevealed(true);
      return partialIds.current.size > 0;
    },
    hasPartial: () => partialIds.current.size > 0,
  }), []);

  const value = useMemo(() => ({ report, revealed }), [report, revealed]);
  return <DateDraftContext.Provider value={value}>{children}</DateDraftContext.Provider>;
});

export default DateDraftScope;
